import { useState, useRef } from 'react';
import { Box, Popover, Typography, Chip, Stack } from '@mui/material';
import type { VocabularyWord } from '../../types/api.types';

interface HighlightedTextProps {
  text: string;
  vocabularyWords: VocabularyWord[];
  segmentId: number;
}

export const HighlightedText = ({ text, vocabularyWords, segmentId: _segmentId }: HighlightedTextProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredWord, setHoveredWord] = useState<VocabularyWord | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // vocabularyWords is already pre-filtered to the current segment by useVocabularyBySegment.
  // The backend's GetVocabularyBySegment does not preload word.segments, so filtering by
  // word.segments here would incorrectly exclude every word. Use the list as-is.
  const segmentWords = vocabularyWords;

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, word: VocabularyWord) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setAnchorEl(event.currentTarget);
    setHoveredWord(word);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      setHoveredWord(null);
    }, 120);
  };

  const handlePopoverMouseEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  };

  // Function to highlight words in the text
  const highlightWords = () => {
    if (!text || segmentWords.length === 0) {
      return <span>{text}</span>;
    }

    // Create a mapping of word positions
    const wordPositions: Array<{ start: number; end: number; word: VocabularyWord }> = [];
    
    segmentWords.forEach(vocabWord => {
      const wordLower = vocabWord.word.toLowerCase();
      const textLower = text.toLowerCase();
      let startIndex = 0;

      // Find all occurrences of the word
      while ((startIndex = textLower.indexOf(wordLower, startIndex)) !== -1) {
        wordPositions.push({
          start: startIndex,
          end: startIndex + vocabWord.word.length,
          word: vocabWord,
        });
        startIndex += vocabWord.word.length;
      }
    });

    // Sort by start position
    wordPositions.sort((a, b) => a.start - b.start);

    // Build highlighted text elements
    const elements: React.ReactElement[] = [];
    let lastIndex = 0;

    wordPositions.forEach((pos, idx) => {
      // Add text before this word
      if (pos.start > lastIndex) {
        elements.push(
          <span key={`text-${idx}`}>{text.substring(lastIndex, pos.start)}</span>
        );
      }

      // Add highlighted word
      const wordText = text.substring(pos.start, pos.end);
      const difficultyColor = getDifficultyColor(pos.word.difficultyLevel);
      
      elements.push(
        <Box
          key={`word-${idx}`}
          component="mark"
          onMouseEnter={(e) => handleMouseEnter(e, pos.word)}
          onMouseLeave={handleMouseLeave}
          sx={{
            display: 'inline',
            backgroundColor: difficultyColor,
            cursor: 'pointer',
            padding: '2px 4px',
            borderRadius: '3px',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'primary.light',
              boxShadow: 1,
            },
          }}
        >
          {wordText}
        </Box>
      );

      lastIndex = pos.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      elements.push(
        <span key="text-end">{text.substring(lastIndex)}</span>
      );
    }

    return <>{elements}</>;
  };

  const getDifficultyColor = (level: number): string => {
    if (level === 0) return '#fff3cd'; // New - yellow
    if (level <= 2) return '#ffd4a3'; // Learning - orange
    if (level <= 4) return '#b3d9ff'; // Practicing - blue
    return '#c3e6cb'; // Mastered - green
  };

  const getDifficultyLabel = (level: number): string => {
    if (level === 0) return 'New';
    if (level <= 2) return 'Learning';
    if (level <= 4) return 'Practicing';
    return 'Mastered';
  };

  return (
    <>
      <Box
        sx={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.8,
        }}
      >
        {highlightWords()}
      </Box>

      {/* Hover Popover — opens above the word to avoid clipping at the bottom of the text area */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMouseLeave}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
        }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handleMouseLeave,
            sx: { pointerEvents: 'auto', maxWidth: 300 },
          },
        }}
      >
        {hoveredWord && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {hoveredWord.word}
            </Typography>
            <Typography variant="body1" color="primary" gutterBottom sx={{ fontStyle: 'italic' }}>
              {hoveredWord.translation}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={getDifficultyLabel(hoveredWord.difficultyLevel)}
                size="small"
                color={hoveredWord.difficultyLevel >= 5 ? 'success' : 'default'}
              />
              <Chip
                label={`${hoveredWord.reviewCount} reviews`}
                size="small"
                variant="outlined"
              />
            </Stack>
            {hoveredWord.segments && hoveredWord.segments.length > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Appears in {hoveredWord.segments.length} segments
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </>
  );
};
